import errorsListData from 'src/views/abi/component/entry-form/errors-list.json';

// Known PGA agency codes from errors-list.json (sorted longest-first for greedy matching)
const KNOWN_PGA_CODES = [
  'APH - CORE',
  'APH - LACEY',
  'APH-LACEY',
  'NHTSA',
  'NMFS',
  'FSIS',
  'EPA',
  'FDA',
  'AMS',
  'ATF',
  'CDC',
  'CPS',
  'DEA',
  'DTC',
  'FWS',
  'OMC',
  'TTB',
  'ALL',
];

// Top-level record types (own their own position)
const TOP_LEVEL_TYPES = ['SUMMRY', 'TOTALS', 'LINITM'];

// Sub-record types (nest under LINITM)
const SUB_RECORD_TYPES = [
  'TARIFF', 'TARQTY', 'OI', 'ADVAL', 'SPECIF', 'MPFVAL', 'HMFVAL',
  'PG01', 'PG02', 'PG03', 'PG04', 'PG05', 'PG06', 'PG07', 'PG08',
  'PG09', 'PG10', 'PG11', 'PG12', 'PG13', 'PG14', 'PG15', 'PG16',
  'PG17', 'PG18', 'PG19', 'PG20', 'PG21', 'PG22', 'PG23', 'PG24',
  'PG25', 'PG26', 'PG27', 'PG28', 'PG29', 'PG30', 'PG31',
  'CA10', 'CA40', 'CA60',
];

// ---------------------------------------------------------------------------
// extractPgaCodes
// ---------------------------------------------------------------------------
// PG01 REF ID format: {3-digit-agency-num}{pgaCode}{programCode}[suffix]
// e.g. "001FDACOS" → agencyNum="001", pgaCode="FDA", programCode="COS"
// Strategy: strip leading 3-digit number, then greedy-match known PGA codes
export const extractPgaCodes = (refId) => {
  if (!refId || typeof refId !== 'string') {
    return { agencyNum: '', pgaCode: '', programCode: '' };
  }

  const trimmed = refId.trim();

  // Strip leading 3-digit agency number
  const agencyNumMatch = trimmed.match(/^(\d{3})(.*)/);
  if (!agencyNumMatch) {
    return { agencyNum: '', pgaCode: '', programCode: trimmed };
  }

  const agencyNum = agencyNumMatch[1];
  const remainder = agencyNumMatch[2]; // e.g. "FDACOS", "FDAFOOPRO"

  // Greedy match known pgaCode at start of remainder
  const matchedPgaCode = KNOWN_PGA_CODES.find((code) =>
    remainder.toUpperCase().startsWith(code.toUpperCase())
  );

  if (!matchedPgaCode) {
    return { agencyNum, pgaCode: remainder, programCode: '' };
  }

  const programRemainder = remainder.slice(matchedPgaCode.length); // e.g. "COS", "FOOPRO"

  // Try to find a valid programCode from errors-list for this pgaCode
  const knownPrograms = [
    ...new Set(
      (errorsListData.errors || [])
        .filter(
          (e) =>
            e.pgaCode?.toUpperCase() === matchedPgaCode.toUpperCase() &&
            e.programCode !== '' &&
            e.programCode !== 'ALL'
        )
        .map((e) => e.programCode)
    ),
  ].sort((a, b) => b.length - a.length); // longest first

  const matchedProgram = knownPrograms.find((prog) =>
    programRemainder.toUpperCase().startsWith(prog.toUpperCase())
  );

  return {
    agencyNum,
    pgaCode: matchedPgaCode,
    programCode: matchedProgram || programRemainder,
  };
};

// ---------------------------------------------------------------------------
// categorizeError
// ---------------------------------------------------------------------------
// PGA : P-prefix e.g. PR6, P15, PC5
// ES/CR : everything else — both use same code+name lookup, no need to split
export const categorizeError = (errorCode) => {
  if (!errorCode) return { isPga: false, messageType: 'ES' };

  if (/^P[A-Z0-9]/i.test(errorCode)) {
    return { isPga: true, messageType: 'PGA' };
  }

  return { isPga: false, messageType: 'ES' };
};

// ---------------------------------------------------------------------------
// getErrorDescription  (matches requirement: ES/CR by code+name, PGA by code+name+pgaCode+programCode)
// ---------------------------------------------------------------------------
export const getErrorDescription = (code, name, messageType, pgaCode = '', programCode = '') => {
  if (!code || !name) return null;

  const errors = errorsListData.errors || [];
  const nameLower = name.toLowerCase().trim();

  if (messageType === 'ES' || messageType === 'CR') {
    // Both ES and CR match by code + name (lowercase) — search across both types
    const match = errors.find(
      (e) =>
        e.code === code &&
        (e.messageType === 'ES' || e.messageType === 'CR') &&
        e.name.toLowerCase().trim() === nameLower
    );
    return match?.description || null;
  }

  if (messageType === 'PGA') {
    const pgaErrors = errors.filter(
      (e) =>
        e.code === code &&
        e.messageType === 'PGA' &&
        e.name.toLowerCase().trim() === nameLower
    );

    // Priority 1: exact match — FDA + COS
    const exact = pgaErrors.find(
      (e) => e.pgaCode === pgaCode && e.programCode === programCode
    );
    if (exact) return exact.description;

    // Priority 2: agency + ALL — FDA + ALL
    const agencyAll = pgaErrors.find(
      (e) => e.pgaCode === pgaCode && e.programCode === 'ALL'
    );
    if (agencyAll) return agencyAll.description;

    // Priority 3: universal — ALL + ALL
    const universal = pgaErrors.find(
      (e) => e.pgaCode === 'ALL' && e.programCode === 'ALL'
    );
    if (universal) return universal.description;

    // Priority 4: any empty pgaCode fallback
    const empty = pgaErrors.find((e) => e.pgaCode === '' || e.pgaCode === null);
    return empty?.description || null;
  }

  return null;
};

// ---------------------------------------------------------------------------
// parseRejectionResponse  (Phase 1a core)
// ---------------------------------------------------------------------------
// Accepts raw rejection text, returns hierarchical JSON tree
//
// Line formats:
//   RECORDTYPE, Position:N.N REF ID:XXXX   ← record header
//   RECORDTYPE, Position: REF ID:XXXX       ← record header (no position)
//   CODE: MESSAGE                            ← error on preceding record
//   998: TRANSACTION DATA REJECTED           ← global error (no parent)
//
export const parseRejectionResponse = (textResponse) => {
  if (!textResponse || typeof textResponse !== 'string') return [];

  const lines = textResponse
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const roots = [];          // top-level nodes (SUMMRY, LINITM, TOTALS, global errors)
  let currentRoot = null;    // current top-level node
  let currentSub = null;     // current sub-record under LINITM
  let currentPgaCodes = {    // PGA context extracted from PG01 under current LINITM
    agencyNum: '',
    pgaCode: '',
    programCode: '',
  };

  // Counter for unique IDs
  const idCounters = {};
  const makeId = (type) => {
    idCounters[type] = (idCounters[type] || 0) + 1;
    return `${type}_${idCounters[type]}`;
  };

  // Parse record header line
  // e.g. "SUMMRY, Position:1.0 REF ID:SY2 82021451 82021451"
  // e.g. "PG01, Position: REF ID:001FDACOS                            100.000"
  const parseHeader = (line) => {
    const headerRe = /^([A-Z0-9]+),?\s*Position:\s*([0-9.]*)\s*REF\s*ID:\s*(.*)$/i;
    const m = line.match(headerRe);
    if (!m) return null;

    const type = m[1].toUpperCase();
    const position = m[2].trim() || '';
    const refRaw = m[3].trim();

    // For PG01 lines, there may be trailing quantity after spaces
    // e.g. "001FDACOS                            100.000"
    const qtyMatch = refRaw.match(/^(\S+)\s+([\d.]+)\s*$/);
    let refId = refRaw;
    let quantity = '';
    if (qtyMatch) {
      refId = qtyMatch[1];
      quantity = qtyMatch[2];
    }

    return { type, position, refId, quantity };
  };

  // Parse error line
  // e.g. "205: PRELIM STMT DATE MUST BE IN FUTURE"
  // e.g. "27J: Census Warning"
  // e.g. "998: TRANSACTION DATA REJECTED"
  const parseErrorLine = (line) => {
    const errRe = /^([A-Z0-9]{2,6}):\s*(.+)$/;
    const m = line.match(errRe);
    if (!m) return null;

    const code = m[1];
    const message = m[2].trim();
    const { isPga, messageType } = categorizeError(code);

    return { code, message, isPga, messageType };
  };

  // Attach error to current context
  const attachError = (errorEntry) => {
    const enriched = {
      ...errorEntry,
      ...(errorEntry.isPga
        ? {
            agencyNum: currentPgaCodes.agencyNum,
            pgaCode: currentPgaCodes.pgaCode,
            programCode: currentPgaCodes.programCode,
          }
        : {}),
    };

    if (currentSub) {
      currentSub.errors.push(enriched);
    } else if (currentRoot) {
      currentRoot.errors.push(enriched);
    } else {
      // Global error (no parent, e.g. 998)
      roots.push({
        id: makeId('GLOBAL'),
        type: 'GLOBAL',
        position: '',
        refId: '',
        errors: [enriched],
        children: [],
        isGlobal: true,
      });
    }
  };

  for (const line of lines) {
    const header = parseHeader(line);

    if (header) {
      const { type, position, refId, quantity } = header;
      const node = {
        id: makeId(type),
        type,
        position,
        refId,
        ...(quantity ? { quantity } : {}),
        errors: [],
        children: [],
      };

      if (type === 'PG01') {
        // Extract PGA codes for context on sibling PG records
        const codes = extractPgaCodes(refId);
        currentPgaCodes = codes;
        node.agencyNum = codes.agencyNum;
        node.pgaCode = codes.pgaCode;
        node.programCode = codes.programCode;
      }

      if (TOP_LEVEL_TYPES.includes(type)) {
        // Reset PGA context for new top-level block
        if (type === 'LINITM') {
          currentPgaCodes = { agencyNum: '', pgaCode: '', programCode: '' };
        }
        currentRoot = node;
        currentSub = null;
        roots.push(node);
      } else if (SUB_RECORD_TYPES.includes(type)) {
        currentSub = node;
        if (currentRoot) {
          currentRoot.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        // Unknown type — treat as sub-record
        currentSub = node;
        if (currentRoot) {
          currentRoot.children.push(node);
        } else {
          roots.push(node);
        }
      }
      continue;
    }

    const errorEntry = parseErrorLine(line);
    if (errorEntry) {
      attachError(errorEntry);
      continue;
    }

    // Unrecognised line — skip silently
  }

  return roots;
};

// ---------------------------------------------------------------------------
// buildHierarchy  (alias / wrapper — kept for API surface consistency)
// ---------------------------------------------------------------------------
export const buildHierarchy = (flatText) => parseRejectionResponse(flatText);
